import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import Card from "components/Card/Card";
import KaboodleModal from "components/Modals/KaboodleModal";
import Table from "components/Table/Table";
import Tooltip from "components/Tooltip/Tooltip";
import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SERVICE_TYPE } from "types/DataTypes";
import { useConstants } from "utils/hooks";
import { useSelector } from "utils/redux";

import type { InvoiceableEntity, InvoiceType } from "./InvoiceTypes";
import useQuickBooksConnectionStatus from "./QuickBooks/useQuickBooksConnectionStatus";
import { useInvoice } from "./useInvoice";

const InvoiceHistory = ({
  entity,
  onInvoiceSelect,
  selectedInvoiceId,
  invoiceType = "file_service",
  showCMS = false,
  filterCMSInvoices,
}: {
  entity: InvoiceableEntity;
  onInvoiceSelect: (id: number) => void;
  selectedInvoiceId: number | undefined;
  invoiceType?: InvoiceType["invoice_type"];
  showCMS?: boolean;
  filterCMSInvoices?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { constants } = useConstants();
  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const {
    isConnectionValid,
    isLoading: isQuickBooksLoading,
    connectionMessage,
  } = useQuickBooksConnectionStatus();

  const getServiceTypeLabel = useCallback(
    (serviceType: string | null | undefined) => {
      // handle special cases for lien and section-111 based on entity type and invoiceType
      if (entity.type === SERVICE_TYPE.LIEN) return SERVICE_TYPE.LIEN;
      if (entity.type === "CLIENT") return "CLIENT";

      // handle null/undefined service types
      if (!serviceType) return "N/A";

      if (!constants?.DEFAULT_INVOICE_PRICING_FIELDS) return serviceType;

      const serviceTypeOption = constants.DEFAULT_INVOICE_PRICING_FIELDS.find(
        (option: { value: string | null; label: string }) =>
          option.value === serviceType && option.value !== null,
      );

      return serviceTypeOption?.label || serviceType;
    },
    [constants?.DEFAULT_INVOICE_PRICING_FIELDS, entity.type, invoiceType],
  );

  const [invoiceToDelete, setInvoiceToDelete] = useState<{
    id: number;
    status: string;
  } | null>(null);

  // extract file and service from entity for API compatibility
  const fileId =
    entity?.file && typeof entity.file === "object" && "id" in entity.file
      ? entity.file.id
      : undefined;

  const serviceId = entity?.service;
  const clientId = entity?.client?.id;

  const {
    deleteInvoice,
    voidInvoice,
    voidAndReissueInvoice,
    createInvoice,
    invoices,
  } = useInvoice({
    file: fileId,
    // for future medical type invoices, we want to get all of them on the file, so don't pass in this specific serviceId
    service: invoiceType === "future_medical" ? undefined : serviceId,
    client: clientId,
    // when filterCMSInvoices is true, we want to exclude CMS invoices (is_cms_invoice: false)
    // when filterCMSInvoices is false or undefined, we show all invoices (don't pass is_cms_invoice)
    is_cms_invoice: filterCMSInvoices === true ? false : undefined,
    invoiceType: invoiceType as InvoiceType["invoice_type"],
    skip: !entity,
  });

  const handleVoidOnly = useCallback(async () => {
    if (!invoiceToDelete) return;

    // if the deleted invoice is the currently selected one, clear the invoiceId from the URL first
    if (selectedInvoiceId === invoiceToDelete.id) {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete("invoiceId");
      navigate({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    }

    // use void for finalized invoices, delete for draft invoices
    if (
      invoiceToDelete.status === "Finalized" ||
      invoiceToDelete.status === "Paid"
    ) {
      await voidInvoice(invoiceToDelete.id);
    } else {
      await deleteInvoice(invoiceToDelete.id);
    }

    setInvoiceToDelete(null);
  }, [
    deleteInvoice,
    voidInvoice,
    invoiceToDelete,
    navigate,
    location,
    selectedInvoiceId,
  ]);

  const handleVoidAndReissue = useCallback(async () => {
    if (!invoiceToDelete) return;

    try {
      // for finalized invoices, use void and reissue functionality
      if (
        invoiceToDelete.status === "Finalized" ||
        invoiceToDelete.status === "Paid"
      ) {
        const result = await voidAndReissueInvoice(invoiceToDelete.id, true);

        // if we got a reissued invoice, select it
        if (result?.reissued_invoice?.id) {
          onInvoiceSelect(result.reissued_invoice.id);
        } else if (selectedInvoiceId === invoiceToDelete.id) {
          // if no reissued invoice but the voided invoice was selected, clear selection
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete("invoiceId");
          navigate({
            pathname: location.pathname,
            search: searchParams.toString(),
          });
        }
      } else {
        // for draft invoices, just delete and create a new one
        await deleteInvoice(invoiceToDelete.id);
        const newInvoiceResult = await createInvoice(entity);
        if (newInvoiceResult?.data?.id) {
          onInvoiceSelect(newInvoiceResult.data.id);
        }
      }
    } catch (error) {
      console.error("Error in void and reissue:", error);
    }

    setInvoiceToDelete(null);
  }, [
    invoiceToDelete,
    voidAndReissueInvoice,
    deleteInvoice,
    createInvoice,
    entity,
    onInvoiceSelect,
    selectedInvoiceId,
    navigate,
    location,
  ]);

  const handleCreateInvoice = useCallback(async () => {
    if (!entity) return;
    const result = await createInvoice(entity);
    // select the newly created invoice if creation was successful
    if (result?.data?.id) {
      onInvoiceSelect(result.data.id);
    }
  }, [createInvoice, entity, onInvoiceSelect]);

  const handleRowClick = useCallback(
    (id: number) => {
      if (id) {
        if (id !== selectedInvoiceId) {
          onInvoiceSelect(id);
        }
      }
    },
    [onInvoiceSelect, selectedInvoiceId],
  );

  if (!entity) {
    console.error("InvoiceHistory: entity is undefined");
    return null;
  }

  const DOC_COLUMNS = [
    {
      key: "selected",
      name: "",
      width: "5%",
      resizeable: false,
      renderCell: (props: { row: { id: number } }) => (
        <div className="selected-row">
          {props.row.id === selectedInvoiceId && (
            <span className="material-icons" style={{ color: "#3c1283" }}>
              done
            </span>
          )}
        </div>
      ),
    },
    {
      key: "id",
      name: "KAboodle Invoice ID",
      width: "20%",
      resizeable: false,
      renderCell: (props: { row: { id: number } }) => <div>{props.row.id}</div>,
    },
    {
      key: "quickbooks_invoice_number",
      name: "Invoice #",
      width: "12%",
      resizeable: false,
      renderCell: (props: {
        row: { quickbooks_invoice_number: string | null };
      }) => (
        <div>
          {props.row.quickbooks_invoice_number ? (
            props.row.quickbooks_invoice_number
          ) : (
            <Tooltip content="This field will be blank until the invoice is posted to Quickbooks">
              <span>—</span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      key: "date_posted",
      name: "Posted Date",
      width: showCMS ? "16%" : "18%",
      resizeable: false,
    },
    {
      key: "service_type",
      name: "Service",
      width: "12%",
      resizeable: false,
      renderCell: (props: { row: { service_type: string } }) => (
        <div>{getServiceTypeLabel(props.row.service_type)}</div>
      ),
    },
    ...(showCMS
      ? [
          {
            key: "is_cms_invoice",
            name: "CMS",
            width: "8%",
            resizeable: false,
            renderCell: (props: { row: { is_cms_invoice: boolean } }) => (
              <div style={{ textAlign: "center", margin: "auto" }}>
                {props.row.is_cms_invoice && (
                  <span
                    className="material-icons"
                    style={{ color: "#3c1283", fontSize: "16px" }}
                  >
                    check_circle
                  </span>
                )}
              </div>
            ),
          },
        ]
      : []),
    {
      key: "status",
      name: "Status",
      width: showCMS ? "10%" : "12%",
      resizeable: false,
    },
    {
      key: "payment_status",
      name: "Payment Status",
      width: showCMS ? "16%" : "18%",
      resizeable: false,
    },
    {
      key: "actions",
      name: "Actions",
      width: showCMS ? "10%" : "10%",
      resizeable: false,
      renderCell: (props: { row: { id: number; status: string } }) => {
        const isVoided = props.row.status === "Voided";
        const isFinalized =
          props.row.status === "Finalized" || props.row.status === "Paid";
        const actionTitle = isFinalized ? "Void" : "Delete";
        const hasVoidPermissions =
          loggedInUser?.has_invoice_voiding_permissions;

        // never show actions for voided invoices
        // hide void action for finalized invoices if user doesn't have voiding permissions
        const showAction = !isVoided && (!isFinalized || hasVoidPermissions);

        return (
          <div className="action-cell">
            {showAction && (
              <div
                className={`material-icons ${isFinalized ? "void" : "delete"}`}
                title={actionTitle}
                onClick={(e) => {
                  e.stopPropagation();
                  setInvoiceToDelete({
                    id: props.row.id,
                    status: props.row.status,
                  });
                }}
              >
                {isFinalized ? "remove_circle" : "delete"}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className={`card form audit-and-consulting-card`}>
        <div className="card__header">
          Invoice History
          <Tooltip
            content={
              isQuickBooksLoading
                ? "Loading QuickBooks connection..."
                : !isConnectionValid
                  ? connectionMessage
                  : ""
            }
            variant="info"
          >
            <ButtonIcon
              kind="neutral"
              size="small"
              onClick={handleCreateInvoice}
              disabled={isQuickBooksLoading || !isConnectionValid}
            >
              add_circle
            </ButtonIcon>
          </Tooltip>
        </div>

        <Table
          className="table documents-table document-audit-table invoices-table"
          columns={DOC_COLUMNS}
          rows={invoices}
          rowKeyGetter={(row) => row.id}
          onCellClick={({ row }) => handleRowClick(row.id)}
          selectedRows={
            selectedInvoiceId ? new Set([selectedInvoiceId]) : new Set()
          }
          onSelectedRowsChange={(rows) => {
            const selectedId = Array.from(rows)[0];
            if (selectedId) {
              handleRowClick(Number(selectedId));
            }
          }}
          rowClass={(row) =>
            row.status === "Voided" ? "voided-row" : undefined
          }
        />
      </div>

      {invoiceToDelete && (
        <KaboodleModal
          isOpen={!!invoiceToDelete}
          onClose={() => setInvoiceToDelete(null)}
          footer={
            <div className="flex jc-c">
              {invoiceToDelete.status === "Finalized" ||
              invoiceToDelete.status === "Paid" ? (
                <>
                  <Button className="delete" onClick={handleVoidAndReissue}>
                    Void and Reissue
                  </Button>
                  <Button className="delete" onClick={handleVoidOnly}>
                    Void Only
                  </Button>
                  <Button onClick={() => setInvoiceToDelete(null)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button className="delete" onClick={handleVoidOnly}>
                    Confirm Delete
                  </Button>
                  <Button onClick={() => setInvoiceToDelete(null)}>
                    Cancel
                  </Button>
                </>
              )}
            </div>
          }
        >
          <Card
            heading={
              invoiceToDelete.status === "Finalized" ||
              invoiceToDelete.status === "Paid"
                ? "Void Invoice"
                : "Delete Invoice"
            }
          >
            <p>
              {invoiceToDelete.status === "Finalized" ||
              invoiceToDelete.status === "Paid"
                ? "Are you sure you want to void this invoice? This will void the invoice in both KAboodle and QuickBooks."
                : "Are you sure you want to delete this invoice? This will permanently remove the invoice from KAboodle."}
            </p>
          </Card>
        </KaboodleModal>
      )}
    </>
  );
};

export default InvoiceHistory;
